import { isTikTokChannel } from 'src/constants/channel.constants';
import { TikTokRichMessage } from 'src/pages/TikTok/components/Capabilities/TikTokRichMessage.component';
import { ArrowUturnRightIcon, CheckIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NIL as NIL_UUID } from 'uuid';
import { eChannelType } from '../../../../../../../../providers/SocketsProvider/Sockets.enums.ts';
import { eAlertType } from '../../../../../../../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../../../../../../../providers/ToastProvider/Toast.hook.ts';
import { useChatContext } from '../../../../../../Chat.hook.ts';
import { EventType, MessageState } from '../../../../../../Chat.enum.ts';
import {
  isUnsupportedContentNotice,
  isWapiUnsupportedInfoNote,
} from '../../../../../../Chat.utils.ts';
import { ChatService } from '../../../../../../Chat.service.ts';
import { tAttachment, tChatEvent } from '../../../../../../Chat.types.ts';
import useMessageBubbleMenu from '../MessageBubbleMenu/MessageBubbleMenu.hook.tsx';
import MessageMenu from './components/MessageMenu/MessageMenu.component.tsx';
import BubbleSenderLabel from './components/BubbleSenderLabel/BubbleSenderLabel.component.tsx';

import { Skeleton } from 'react-daisyui';
import { useModalsContext } from '../../../../../../../../providers/ModalsProvider/Modals.context.ts';
import useChatActions from '../../../../../../Chat.actions.ts';
import MessageByContentType from './components/MessageByContentType.component.tsx';
import AdReferralCard from './components/AdReferralCard/AdReferralCard.component.tsx';
import { QuotedMessage } from './components/QuotedMessage/QuotedMessage.component.tsx';
import { getContentCarouselReply } from './components/QuotedMessage/quotedCarousel.functions.ts';
import {
  findResolvingMessage,
  hasVisualMedia,
  noticeSourceOf,
  resolvedTimeFormat,
  resolveIncomingMetaErrorNotice,
  resolveOutgoingMetaErrorNotice,
} from './MessageBubbleV2.functions.ts';
import ReactionButton from './components/Reactions/ReactionButton.component.tsx';
import ReactionPill from './components/Reactions/ReactionPill.component.tsx';
import { canViewReactionsOn } from './components/Reactions/reactionChannels.ts';
import useEventReaction from './hooks/useEventReaction.hook.ts';
import useSenderTitle from './hooks/useSenderTitle.hook.ts';
import MessageSources from '../../../../../../../../components/MessageSources/MessageSources.component.tsx';
import {
  META_ERROR_NOTICE_NAMESPACE,
  noticeStatusKey,
} from '../../../../../../../../utils/metaErrorNotice.ts';
import CatalogMetaErrorNotice from './components/MetaErrorNotice/CatalogMetaErrorNotice.component.tsx';
import ScheduledLegend from '../ScheduledLegend/ScheduledLegend.component.tsx';
import BubbleMeta, {
  tBubbleMetaStatus,
} from './components/BubbleMeta/BubbleMeta.component.tsx';
import useBubbleDate from '../../../../bubbleDate/useBubbleDate.hook.ts';
import useUnsupportedNoticeAnalytics from 'src/hooks/analytics/chat/useUnsupportedNoticeAnalytics.hook';
import {
  countedNoticeOf,
  serverNoticeOf,
} from 'src/hooks/analytics/chat/unsupportedNoticeAnalytics.functions';
import './MessageBubbleV2.styles.scss';

function MessageBubbleV2({
  event,
  innerRef,
  scheduledOrigin,
  groupStart = true,
}: {
  event: tChatEvent;
  // Widened from RefObject: MessageContainer now hands down a callback ref
  // that feeds both the pagination anchor and the event-id node map (SRE-3328).
  innerRef: React.Ref<HTMLDivElement>;
  /** INT-1505: the scheduled card this message was sent from, if any. */
  scheduledOrigin?: tChatEvent;
  /**
   * First bubble of a run from the same sender: it carries the tail and the
   * sender line. Later bubbles of the run sit 2 px below it without either.
   */
  groupStart?: boolean;
}) {
  const { message, content, id, createdAt, createdBy } = event;
  const { showCheckStatus, showErrorMessage } = useMessageBubbleMenu();
  const { selectedLeadForChatModal } = useModalsContext();
  const {
    isResending,
    selectedChat,
    messages,
    resendIds,
    selectMessageToReply,
    chatEvents,
  } = useChatContext();
  const { addAlert } = useToastContext();
  const { decryptAttachment } = useChatActions();
  const { t } = useTranslation(['chat/chat', META_ERROR_NOTICE_NAMESPACE]);

  const isWapiChannel = selectedChat?.integration === eChannelType.WHATSAPP_API;
  // INT-1702: Messenger and Instagram show the contact's reactions, but
  // Leadsales only sends reactions through WhatsApp API.
  const canViewReactions = canViewReactionsOn(selectedChat?.integration);
  // BUG-704: 3063/3064 are synthetic informational notes with no real content —
  // hide the emoji-react affordance (the per-message menu is hidden separately
  // in MessageMenu). There is nothing to react to.
  const isUnsupportedInfoNote = isWapiUnsupportedInfoNote(message?.error);
  // BUG-1063: a deleted message is the same case — there is nothing left to
  // react to. MessageMenu hides itself for these; this is its sibling gate.
  const isDeletedMessage =
    event.type === EventType.OUTBOUND_DELETED ||
    event.type === EventType.INBOUND_DELETED;
  // INT-1503: Meta refused this outgoing message. The notice at the bottom of
  // the bubble replaces the status icon, its hover tooltip and the rail below.
  const metaErrorNotice = useMemo(
    () =>
      resolveOutgoingMetaErrorNotice(
        noticeSourceOf(event),
        selectedChat?.integration,
      ),
    [
      message?.fromMe,
      message?.state,
      message?.error,
      event.errors,
      selectedChat?.integration,
    ],
  );
  // INT-1504: a later message got through, so the notice has done its job.
  // The header keeps "No entregado" (this message never arrived) and the
  // notice gives way to a quiet "Resuelto" line.
  // INT-1500 U5: a message in Meta's review did not fail, so nothing
  // resolves it; it waits for its own delivery or refusal.
  // INT-1683: neither does a refused image: a later text does not deliver it.
  const resolvingMessage = useMemo(
    () =>
      metaErrorNotice &&
      metaErrorNotice.variant !== 'pending' &&
      !metaErrorNotice.keepsNoticeAfterLaterSend
        ? findResolvingMessage(event, chatEvents ?? [])
        : undefined,
    [metaErrorNotice, event.id, event.createdAt, chatEvents],
  );
  const showMetaErrorNotice = Boolean(metaErrorNotice) && !resolvingMessage;
  // INT-1506: the lead's message did not reach us whole (131052, 131060,
  // 131051), or the business's own echo that Meta did not share (INT-1500
  // U4). The header stays as on any message; the notice goes at the bottom.
  const incomingMetaErrorNotice = useMemo(
    () =>
      resolveIncomingMetaErrorNotice(
        noticeSourceOf(event),
        selectedChat?.integration,
      ),
    [
      message?.fromMe,
      message?.state,
      message?.error,
      event.errors,
      selectedChat?.integration,
    ],
  );
  const myReaction = event.reactions?.find((r) => r.fromMe);
  const handleReaction = useEventReaction(event);

  const [quotedMessage, setQuotedMessage] = useState<tChatEvent | undefined>(
    undefined,
  );
  const [loadingAttachments, setLoadingAttachments] = useState<boolean>(false);

  const messageSelected = resendIds.includes(id);

  useEffect(() => {
    (async () => {
      try {
        if (content?.quotedEventID === NIL_UUID || !content?.quotedEventID) {
          return;
        }

        const quotedMessage = messages?.events?.find(
          (message) => message.id === content?.quotedEventID,
        );

        if (quotedMessage) {
          const attachments: tAttachment[] = [];
          const quotedAttachments = quotedMessage?.content?.attachments;

          if (quotedAttachments?.length) {
            for (const attachment of quotedAttachments) {
              const fetchAttachment = async () => {
                setLoadingAttachments(true);

                const decryptedAttachment = await decryptAttachment(attachment);

                attachments.push(decryptedAttachment);
                setLoadingAttachments(false);
              };
              fetchAttachment();
            }
          }

          setQuotedMessage({
            ...quotedMessage,
            content: {
              ...quotedMessage.content,
              attachments,
            },
          });
          return;
        }

        const response = await ChatService.getChatEventById(
          content.quotedEventID,
        );
        setQuotedMessage(response.data);
      } catch (err) {
        addAlert({
          type: eAlertType.ERROR,
          message:
            (err as Error).message || 'Error al obtener el mensaje citado',
        });
      }
    })();
  }, []);

  const {
    title: messageTitle,
    isLeadAgent: isSLBMessage,
    isPhone: isPhoneSender,
  } = useSenderTitle(createdBy);
  const bubbleDate = useBubbleDate();

  const hasReactions = Boolean(
    canViewReactions && event.reactions && event.reactions.length > 0,
  );

  const messageBubbleClassNames = classNames('MessageBubbleV2', {
    'MessageBubbleV2--resending': isResending,
    'MessageBubbleV2--group-start': groupStart,
    'MessageBubbleV2--group-continued': !groupStart,
    'MessageBubbleV2--has-reactions': hasReactions,
  });

  const messageBubbleWrapperClassNames = classNames(
    'MessageBubbleV2__Wrapper',
    {
      'bg-primary/20': messageSelected,
      'col-span-11': isResending,
      'col-span-12': !isResending,
      'chat-end justify-end': message?.fromMe,
      'chat-start': !message?.fromMe,
    },
  );

  const checkContainerClassNames = classNames(
    'MessageBubbleV2__CheckContainer',
    {
      'bg-primary/20': messageSelected,
    },
  );

  // What the message itself shows: a deleted message, a notice that stands in
  // for one Meta did not share and a failed message render their own line
  // instead of the text or the media.
  const rendersMessage =
    event.type !== EventType.OUTBOUND_DELETED &&
    event.type !== EventType.INBOUND_DELETED &&
    !incomingMetaErrorNotice &&
    !(message?.state === MessageState.ERROR && !metaErrorNotice);
  // An image or video bubble is as wide as the media, like an album: the
  // caption and the rest of the bubble wrap to it, as in WhatsApp.
  const isMediaBubble = rendersMessage && hasVisualMedia(content?.attachments);

  const messageBubbleContainerClassNames = classNames(
    'MessageBubbleV2__Container',
    {
      'MessageBubbleV2__Container--outgoing': message?.fromMe && !isSLBMessage,
      'MessageBubbleV2__Container--slb': isSLBMessage,
      'MessageBubbleV2__Container--incoming': !message?.fromMe,
      // The tail marks only the first bubble of a run.
      'MessageBubbleV2__Container--tail-start': groupStart && !message?.fromMe,
      'MessageBubbleV2__Container--tail-end': groupStart && message?.fromMe,
      'MessageBubbleV2__Container--no-tail': !groupStart,
      'MessageBubbleV2__Container--media': isMediaBubble,
      'MessageBubbleV2__Container--with-notice':
        showMetaErrorNotice || Boolean(incomingMetaErrorNotice),
    },
  );

  // The date and time next to the message ("2 oct 9:52 p.m."), with the star,
  // "Editado", the status words and the checks that used to live in the header.
  const metaStatus: tBubbleMetaStatus | undefined =
    metaErrorNotice?.variant === 'pending'
      ? // INT-1500 U5: "<time> · En revisión", not a failure.
        {
          text: t('status.inReview', { ns: META_ERROR_NOTICE_NAMESPACE }),
          tone: 'review',
        }
      : metaErrorNotice
        ? // INT-1500 U10: "No enviado" when Leadsales refused it.
          {
            text: t(noticeStatusKey(metaErrorNotice), {
              ns: META_ERROR_NOTICE_NAMESPACE,
            }),
            tone: 'error',
          }
        : undefined;
  // INT-1500 U4: an echo Meta did not share explains itself in its notice; no
  // status icon claims it failed.
  const metaTicks =
    message?.fromMe && !metaErrorNotice && !incomingMetaErrorNotice
      ? showCheckStatus(message?.state, message?.error)
      : null;
  const metaProps = {
    stamp: bubbleDate.stamp(createdAt),
    spoken: bubbleDate.spoken(createdAt),
    dateTime: createdAt,
    editedLabel: t('messageBubble.edited'),
    isFavorite: Boolean(message?.isFavorite),
    isEdited: Boolean(content?.isEdited),
    status: metaStatus,
    ticks: metaTicks,
  };
  // The stamp shares the last line only when the content ends in text; media
  // without a caption, notes and error cards get it on a row of its own.
  const isTikTokRich =
    isTikTokChannel(selectedChat?.integration) && Boolean(content?.tiktok);
  const endsWithText =
    rendersMessage && !isTikTokRich && !isEmpty(content?.text);
  // An invisible copy of the stamp reserves its place at the end of the last
  // line. A caption carries it inside its text, so the bubble never grows past
  // the media. A plain message carries two and its styles show one: beside the
  // text block in a wide conversation, so a one-line message keeps the stamp
  // on its line, and inside the text in a narrow one, where a stamp beside the
  // block no longer fits the row (BUG-1046).
  const metaSpacer = endsWithText ? (
    <BubbleMeta {...metaProps} variant="spacer" />
  ) : null;

  const hasQuotedMessage =
    content?.quotedEventID && content?.quotedEventID !== NIL_UUID;
  const quotedMessageTitle = quotedMessage?.message?.fromMe
    ? 'Tú'
    : (selectedLeadForChatModal?.name ??
      quotedMessage?.message?.participant?.name);

  const forwardedLabel = message?.isForwarded
    ? t('messageBubble.forwarded')
    : '';

  const handle = selectedLeadForChatModal?.handle || '';
  const channelType = selectedLeadForChatModal?.chat?.integration;
  // TikTok 1:1 handles are long opaque strings that trigger the WhatsApp-group
  // heuristic. Exclude TIKTOK so the per-message sender label is never shown
  // for TikTok 1:1 chats. The heuristic remains active for all other channels.
  const isGroup =
    channelType !== eChannelType.TIKTOK &&
    (handle.includes('-') || handle.length > 13);
  // NOTE — no secondary per-site JSX guard (e.g. `channelType !== TIKTOK`) is
  // needed inside the participant-label block below. TypeScript narrows the
  // type at the `isGroup` branch: because `eChannelType.TIKTOK` is excluded by
  // the `isGroup` definition above, any guard `channelType !== eChannelType.TIKTOK`
  // inside an `isGroup`-truthy block would produce TS2367 ("no overlap") and
  // would never be reached. `isGroup` is the single source of truth for whether
  // the participant-label renders — do NOT add a redundant secondary guard here.

  // The line above the text: who sent it (first bubble of a run), "Reenviado",
  // or the group participant. A bubble with none of them has no line at all.
  const participantName =
    message?.participant?.name || message?.participant?.handle;
  const showSenderLabel =
    groupStart && message?.fromMe && !message?.isForwarded;
  const showParticipant =
    groupStart && !message?.fromMe && isGroup && Boolean(participantName);
  const showLabelLine = Boolean(
    showSenderLabel || message?.isForwarded || showParticipant,
  );
  // INT-1704 (KTD3): the contact's 1004 is said once, in the body notice
  // (MessageByContentType); its 2001–2009 never reached this rail. The
  // business 1004 keeps it, so 1004 stays in showErrorMessage.
  const isInboundUnsupportedNotice =
    message?.fromMe === false && isUnsupportedContentNotice(message?.error);
  const errorRail =
    (!message?.fromMe || message?.state === MessageState.ERROR) &&
    !metaErrorNotice &&
    !incomingMetaErrorNotice &&
    !isInboundUnsupportedNotice
      ? showErrorMessage(message?.error ?? '')
      : null;
  // INT-1691: the incoming WhatsApp API notice always stands for content that
  // did not arrive, the business's own echoes included (KTD6). It can resolve
  // from the error row alone (message.error empty), so it falls back to the
  // Meta code it showed. The rail counts only content we cannot show, never a
  // failed send. The status icon's tooltip reports nothing.
  useUnsupportedNoticeAnalytics({
    eventId: id,
    chatId: event.chatID,
    createdAt,
    notice: incomingMetaErrorNotice
      ? (serverNoticeOf(message?.error) ??
        serverNoticeOf(incomingMetaErrorNotice.displayCode))
      : errorRail
        ? countedNoticeOf(message ?? {})
        : null,
  });

  return (
    <div className={messageBubbleClassNames} ref={innerRef}>
      {isResending && (
        <div className={checkContainerClassNames}>
          <input
            type="checkbox"
            className="MessageBubbleV2__CheckContainer__Checkbox"
            checked={messageSelected}
            onChange={() => {
              selectMessageToReply(messageSelected, id);
            }}
          />
        </div>
      )}

      <div className={messageBubbleWrapperClassNames}>
        <div
          className={classNames('group flex items-end gap-1', {
            'flex-row-reverse': message?.fromMe,
          })}
        >
          <div className={messageBubbleContainerClassNames}>
            {/* Top right of the bubble, as in WhatsApp. */}
            <MessageMenu chatEvent={event} placement="end" />

            {/* Hangs off the bubble's bottom edge instead of taking a row. */}
            {hasReactions && (
              <div
                className={classNames('MessageBubbleV2__Reactions', {
                  'MessageBubbleV2__Reactions--end': message?.fromMe,
                  'MessageBubbleV2__Reactions--start': !message?.fromMe,
                })}
              >
                <ReactionPill
                  reactions={event.reactions ?? []}
                  fromMe={!!message?.fromMe}
                  onToggleReaction={isWapiChannel ? handleReaction : undefined}
                />
              </div>
            )}

            {showLabelLine ? (
              <div className="MessageBubbleV2__Label">
                {showSenderLabel ? (
                  <BubbleSenderLabel
                    title={messageTitle}
                    isLeadAgent={isSLBMessage}
                    showPhoneIcon={isPhoneSender}
                    fitContent
                  />
                ) : null}

                {message?.isForwarded && (
                  <span className="MessageBubbleV2__Forwarded MessageBubbleV2__accent">
                    <ArrowUturnRightIcon className="MessageBubbleV2__Forwarded__Icon" />
                    {forwardedLabel}
                  </span>
                )}

                {showParticipant ? (
                  <div
                    data-testid="participant-label"
                    className="text-primary text-xs"
                  >
                    {participantName}
                  </div>
                ) : null}
              </div>
            ) : null}

            {hasQuotedMessage ? (
              <div className="MessageBubbleV2__QuotedMessage">
                <h6 className="MessageBubbleV2__QuotedMessage__Title">
                  {quotedMessageTitle}:
                </h6>

                {quotedMessage && !loadingAttachments ? (
                  <QuotedMessage
                    {...quotedMessage}
                    carouselCardIndex={
                      getContentCarouselReply(content)?.card_index
                    }
                  />
                ) : (
                  <div className="w-full h-10">
                    <Skeleton className="w-full h-full rounded" />
                  </div>
                )}
              </div>
            ) : null}

            {/*
            Link preview card — a Click-to-WhatsApp ad the lead tapped to open
            the chat (title/description/url describe the ad), or an ordinary URL
            preview. Both arrive in the same `linkPreview` shape (INT-1199).

            Layout is a fixed 48px square thumbnail beside clamped text, NOT a
            full-width banner: WhatsApp ships `externalAdReply.thumbnail` as a
            ~50x50 / ~1.4KB JPEG, so stretching it to the card width is a ~6x
            upscale that renders as mush. (The sibling AdReferralCard does use a
            banner because Meta's CTM/CTIM payload carries full-resolution CDN
            URLs — different data, different layout.)

            `items-start` + explicit w-12/h-12 are load-bearing: a flex row
            defaults to align-items:stretch, which combined with object-cover and
            no width stretched the 50x50 thumbnail into a tall, cropped vertical
            sliver. The description is clamped because an ad body is the full
            marketing copy — unclamped it buried the message itself. (INT-1201)

            `ph-no-capture`: session replay records `href` unmasked, and the
            block class keeps the URL out of it.
          */}
            {content?.linkPreview?.url ? (
              <a
                href={content?.linkPreview.url}
                target="_blank"
                rel="noreferrer"
                className="MessageBubbleV2__LinkPreview ph-no-capture flex items-start gap-2 p-1.5 bg-neutral mix-blend-multiply rounded overflow-hidden hover:brightness-95"
                data-testid="link-preview-card"
              >
                {content?.linkPreview.thumbnail && (
                  <img
                    src={`data:image/jpeg;base64,${content?.linkPreview.thumbnail?.replace(/^,/, '')}`}
                    alt=""
                    aria-hidden
                    className="w-12 h-12 shrink-0 rounded object-cover"
                    loading="lazy"
                    data-testid="link-preview-thumbnail"
                  />
                )}
                <div className="min-w-0 flex-1">
                  {content?.linkPreview.title && (
                    <p
                      className="font-bold text-sm line-clamp-1"
                      data-testid="link-preview-title"
                    >
                      {content?.linkPreview.title}
                    </p>
                  )}
                  {content?.linkPreview.description && (
                    <p
                      className="break-words text-xs line-clamp-2 opacity-70"
                      data-testid="link-preview-description"
                    >
                      {content?.linkPreview.description}
                    </p>
                  )}
                </div>
              </a>
            ) : null}

            {/*
            Meta ad-referral context (IG CTIM / Messenger CTM). Inbound-only —
            these messages are lead-initiated, so the card never renders on an
            outgoing/echo bubble. Placed after LinkPreview deliberately:
            LinkPreview is message-body content, the ad card is contextual
            metadata. Blocks stack, so quoted + linkPreview + adReferral can all
            render. The card's own render floor returns null for a payload with
            no creative and no title. (INT-972)
          */}
            {!message?.fromMe && content?.adReferral ? (
              <AdReferralCard adReferral={content.adReferral} />
            ) : null}

            {/*
              In a wide conversation the text wraps at 65% of it and the bubble
              may grow by the stamp's width: the copy in StampSlot reserves that
              room beside the text, so a message that fits on one line keeps
              its date and time on that line. In a narrow one the text takes
              the whole row and the copy inside it closes its last line. Under
              an image or video the caption wraps to the media and only carries
              the copy inside.
            */}
            <div
              className={classNames('MessageBubbleV2__Body', {
                'MessageBubbleV2__Body--inline': endsWithText && !isMediaBubble,
              })}
            >
              <div className="MessageBubbleV2__Content">
                {isTikTokRich && content?.tiktok ? (
                  <TikTokRichMessage content={content.tiktok} />
                ) : (
                  <MessageByContentType event={event} textEnd={metaSpacer} />
                )}
              </div>
              {metaSpacer && !isMediaBubble ? (
                <span className="MessageBubbleV2__StampSlot">{metaSpacer}</span>
              ) : null}
              <BubbleMeta
                {...metaProps}
                variant={endsWithText ? 'overlay' : 'block'}
              />
            </div>

            {/*
            AGI-243 "Ver fuentes": internal-only, discreet control listing what
            the Lead Agent grounded this reply on. Gated on isSLBMessage so it
            only ever appears under a Lead Agent turn — a message with no
            sources renders nothing (MessageSources' own render floor).
          */}
            {isSLBMessage && <MessageSources sources={event.sources} />}

            {/* INT-1505: above the notice, so a refused scheduled message reads
              "programado por …" before why it was refused. */}
            {scheduledOrigin && <ScheduledLegend followUp={scheduledOrigin} />}

            {metaErrorNotice && showMetaErrorNotice && (
              <CatalogMetaErrorNotice notice={metaErrorNotice} eventId={id} />
            )}
            {incomingMetaErrorNotice && (
              <CatalogMetaErrorNotice
                notice={incomingMetaErrorNotice}
                eventId={id}
              />
            )}
            {resolvingMessage && (
              <p
                className="MessageBubbleV2__Resolved"
                data-testid="meta-error-resolved"
              >
                <CheckIcon
                  className="MessageBubbleV2__Resolved__Icon"
                  aria-hidden="true"
                />
                <span>
                  {t('resolved', {
                    ns: META_ERROR_NOTICE_NAMESPACE,
                    time: resolvedTimeFormat(resolvingMessage.createdAt),
                  })}
                </span>
              </p>
            )}
          </div>
          {isWapiChannel && !isUnsupportedInfoNote && !isDeletedMessage && (
            <ReactionButton
              onSelect={handleReaction}
              currentEmoji={myReaction?.emoji}
            />
          )}
        </div>
        {/*
          INT-1250 / BUG-908 R16 — this rail states a failure, so it renders
          only while the message is actually failed. An outbound bubble that
          moved on (delivered after a late webhook, or put back in SENDING by a
          retry) must not still say "no se pudo enviar": under a blue
          double-check that is simply false, and mid-retry it invites the
          second send that puts the message on the customer's phone twice.

          The direction test is load-bearing, not defensive. This one rail
          renders every inbound error too, and for MESSAGE_DECRYPTION_ERROR it
          is the only content the bubble has -- MessageByContentType
          deliberately excludes that code. An inbound MESSAGE_NOT_SUPPORTED is
          no longer one of them: the body shows its notice and this rail stays
          off for it (INT-1704). Gating on
          ERROR rather than listing the success states also sidesteps the
          DELIVERED/DELIVERY spelling split between this enum and the database.

          It renders only with something to say: an empty rail under every
          inbound bubble used to add the wrapper's 4 px gap between messages.
        */}
        {errorRail ? (
          <div className="text-sm text-orange-400 italic ">{errorRail}</div>
        ) : null}
      </div>
    </div>
  );
}

export default React.memo(MessageBubbleV2);
